import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { config } from '@/config/config'
import { ScheduledTemplate } from '@/types/schedule'
import { ArrowLeft, Calendar, Loader2, Plus, Pencil, Trash2 } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'

interface ScheduleTemplateDialogProps {
  templateId: string
  templateName: string
  children: React.ReactNode
  onScheduleChange?: (schedules: ScheduledTemplate[]) => void
}

type ViewMode = 'list' | 'form'

export const ScheduleTemplateDialog = observer(
  function ScheduleTemplateDialog({
    templateId,
    templateName,
    children,
    onScheduleChange,
  }: ScheduleTemplateDialogProps) {
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [deleting, setDeleting] = useState(false)
    const [schedules, setSchedules] = useState<ScheduledTemplate[]>([])
    const [editingSchedule, setEditingSchedule] =
      useState<ScheduledTemplate | null>(null)
    const [viewMode, setViewMode] = useState<ViewMode>('form')

    // Form state
    const [cronExpression, setCronExpression] = useState('0 8 * * *')
    const [timezone, setTimezone] = useState('UTC')
    const [enabled, setEnabled] = useState(true)

    const cronPresets = [
      { label: 'Daily at 8:00 AM', value: '0 8 * * *' },
      { label: 'Daily at 9:00 AM', value: '0 9 * * *' },
      { label: 'Daily at 6:00 PM', value: '0 18 * * *' },
      { label: 'Every Monday at 9:00 AM', value: '0 9 * * 1' },
      { label: 'Every weekday at 9:00 AM', value: '0 9 * * 1-5' },
      { label: 'First day of month at 9:00 AM', value: '0 9 1 * *' },
      { label: 'Custom', value: 'custom' },
    ]

    const timezones = [
      'UTC',
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'Europe/London',
      'Europe/Paris',
      'Asia/Tokyo',
      'Australia/Sydney',
    ]

    const fetchSchedules = useCallback(async () => {
      setLoading(true)
      try {
        const response = await fetch(
          `${config.apiEndpoint}/api/scheduled_templates?templateId=${templateId}`,
          {
            credentials: 'include',
          }
        )

        if (!response.ok) {
          throw new Error('Failed to fetch schedules')
        }

        const fetchedSchedules: ScheduledTemplate[] = await response.json()
        setSchedules(fetchedSchedules)

        // Determine initial view mode based on schedule count
        if (fetchedSchedules.length === 0) {
          // No schedules: show create form
          setViewMode('form')
          setEditingSchedule(null)
          resetFormToDefaults()
        } else if (fetchedSchedules.length === 1) {
          // One schedule: show edit form for it
          setViewMode('form')
          setEditingSchedule(fetchedSchedules[0])
          populateFormFromSchedule(fetchedSchedules[0])
        } else {
          // Multiple schedules: show list
          setViewMode('list')
          setEditingSchedule(null)
        }
      } catch (error) {
        console.error('Failed to fetch schedules:', error)
      } finally {
        setLoading(false)
      }
    }, [templateId])

    const resetFormToDefaults = () => {
      setCronExpression('0 8 * * *')
      setTimezone('UTC')
      setEnabled(true)
    }

    const populateFormFromSchedule = (schedule: ScheduledTemplate) => {
      setCronExpression(schedule.cronExpression)
      setTimezone(schedule.timezone)
      setEnabled(schedule.enabled)
    }

    useEffect(() => {
      if (open) {
        fetchSchedules()
      }
    }, [open, fetchSchedules])

    const handleSave = async () => {
      setSaving(true)
      try {
        const isUpdate = editingSchedule !== null
        const url = isUpdate
          ? `${config.apiEndpoint}/api/scheduled_templates/${editingSchedule.id}`
          : `${config.apiEndpoint}/api/scheduled_templates`

        const response = await fetch(url, {
          method: isUpdate ? 'PUT' : 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            templateId: isUpdate ? undefined : templateId,
            cronExpression,
            timezone,
            enabled,
          }),
        })

        if (!response.ok) {
          throw new Error('Failed to save schedule')
        }

        const savedSchedule = await response.json()

        // Update local schedules array
        let updatedSchedules: ScheduledTemplate[]
        if (isUpdate) {
          updatedSchedules = schedules.map((s) =>
            s.id === savedSchedule.id ? savedSchedule : s
          )
        } else {
          updatedSchedules = [...schedules, savedSchedule]
        }
        setSchedules(updatedSchedules)
        onScheduleChange?.(updatedSchedules)

        toast.success(
          isUpdate
            ? 'Schedule updated successfully!'
            : 'Schedule created successfully!'
        )

        // After saving, decide what view to show
        if (updatedSchedules.length > 1) {
          setViewMode('list')
          setEditingSchedule(null)
        } else {
          // Stay in form view for single schedule
          setEditingSchedule(savedSchedule)
        }
      } catch (error) {
        toast.error('Failed to save schedule')
        console.error(error)
      } finally {
        setSaving(false)
      }
    }

    const handleDelete = async (scheduleToDelete?: ScheduledTemplate) => {
      const targetSchedule = scheduleToDelete || editingSchedule
      if (!targetSchedule) return

      setDeleting(true)
      try {
        const response = await fetch(
          `${config.apiEndpoint}/api/scheduled_templates/${targetSchedule.id}`,
          {
            method: 'DELETE',
            credentials: 'include',
          }
        )

        if (!response.ok) {
          throw new Error('Failed to delete schedule')
        }

        // Update local schedules array
        const updatedSchedules = schedules.filter(
          (s) => s.id !== targetSchedule.id
        )
        setSchedules(updatedSchedules)
        onScheduleChange?.(updatedSchedules)

        toast.success('Schedule removed successfully!')

        // After deleting, decide what view to show
        if (updatedSchedules.length === 0) {
          setViewMode('form')
          setEditingSchedule(null)
          resetFormToDefaults()
        } else if (updatedSchedules.length === 1) {
          setViewMode('form')
          setEditingSchedule(updatedSchedules[0])
          populateFormFromSchedule(updatedSchedules[0])
        } else {
          setViewMode('list')
          setEditingSchedule(null)
        }
      } catch (error) {
        toast.error('Failed to delete schedule')
        console.error(error)
      } finally {
        setDeleting(false)
      }
    }

    const handleEditSchedule = (schedule: ScheduledTemplate) => {
      setEditingSchedule(schedule)
      populateFormFromSchedule(schedule)
      setViewMode('form')
    }

    const handleAddNewSchedule = () => {
      setEditingSchedule(null)
      resetFormToDefaults()
      setViewMode('form')
    }

    const handleBackToList = () => {
      setViewMode('list')
      setEditingSchedule(null)
    }

    const getPresetValue = () => {
      const preset = cronPresets.find((p) => p.value === cronExpression)
      return preset ? preset.value : 'custom'
    }

    const handlePresetChange = (value: string) => {
      if (value !== 'custom') {
        setCronExpression(value)
      }
    }

    const formatCronForDisplay = (cron: string): string => {
      const preset = cronPresets.find((p) => p.value === cron)
      return preset && preset.value !== 'custom' ? preset.label : cron
    }

    const isCreating = editingSchedule === null
    const showBackButton = viewMode === 'form' && schedules.length > 1

    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{children}</DialogTrigger>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle data-test-id="schedule-dialog-title">
              {viewMode === 'list'
                ? 'Manage Schedules'
                : isCreating
                  ? 'Create Schedule'
                  : 'Edit Schedule'}
            </DialogTitle>
            <DialogDescription>
              Automatically create checklists from "{templateName}" on a
              schedule
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : viewMode === 'list' ? (
            // List view for multiple schedules
            <>
              <div className="space-y-3 py-4">
                {schedules.map((schedule) => (
                  <div
                    key={schedule.id}
                    data-test-id="schedule-list-item"
                    className="flex items-center justify-between p-3 bg-muted rounded-lg"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <Calendar className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">
                          {formatCronForDisplay(schedule.cronExpression)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {schedule.timezone}
                        </p>
                      </div>
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full flex-shrink-0 ${
                          schedule.enabled
                            ? 'bg-success/15 text-success'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {schedule.enabled ? 'Active' : 'Paused'}
                      </span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      data-test-id="schedule-edit-button"
                      onClick={() => handleEditSchedule(schedule)}
                      className="ml-2"
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  data-test-id="schedule-close-button"
                  onClick={() => setOpen(false)}
                >
                  Close
                </Button>
                <Button
                  data-test-id="schedule-add-another-button"
                  onClick={handleAddNewSchedule}
                  className="bg-toast-deep text-forest hover:bg-toast-deep/90"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Schedule
                </Button>
              </DialogFooter>
            </>
          ) : (
            // Form view for creating/editing
            <>
              {showBackButton && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleBackToList}
                  className="w-fit -mt-2 mb-2"
                >
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Back to list
                </Button>
              )}

              <div className="space-y-4 py-4">
                {!isCreating && (
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium">
                        Schedule Active
                      </span>
                      <span
                        data-test-id="schedule-status-badge"
                        className={`text-xs px-2 py-0.5 rounded-full ${
                          enabled
                            ? 'bg-success/15 text-success'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {enabled ? 'Enabled' : 'Paused'}
                      </span>
                    </div>
                    <label
                      data-test-id="schedule-enabled-toggle-label"
                      className="relative inline-flex items-center cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        data-test-id="schedule-enabled-toggle"
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-toast rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-toast"></div>
                    </label>
                  </div>
                )}

                <div className="space-y-2">
                  <label
                    htmlFor="preset"
                    className="text-sm font-medium text-foreground"
                  >
                    Schedule Preset
                  </label>
                  <select
                    id="preset"
                    data-test-id="schedule-preset-select"
                    value={getPresetValue()}
                    onChange={(e) => handlePresetChange(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {cronPresets.map((preset) => (
                      <option key={preset.value} value={preset.value}>
                        {preset.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="cron"
                    className="text-sm font-medium text-foreground"
                  >
                    Cron Expression
                  </label>
                  <input
                    id="cron"
                    type="text"
                    data-test-id="schedule-cron-input"
                    value={cronExpression}
                    onChange={(e) => setCronExpression(e.target.value)}
                    placeholder="0 8 * * *"
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                  <p className="text-xs text-muted-foreground">
                    Format: minute hour day month weekday
                  </p>
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="timezone"
                    className="text-sm font-medium text-foreground"
                  >
                    Timezone
                  </label>
                  <select
                    id="timezone"
                    data-test-id="schedule-timezone-select"
                    value={timezone}
                    onChange={(e) => setTimezone(e.target.value)}
                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                  >
                    {timezones.map((tz) => (
                      <option key={tz} value={tz}>
                        {tz}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <DialogFooter className="flex-col sm:flex-row gap-2">
                {!isCreating && (
                  <Button
                    variant="destructive"
                    data-test-id="schedule-delete-button"
                    onClick={() => handleDelete()}
                    disabled={deleting || saving}
                    className="sm:mr-auto"
                  >
                    {deleting ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Remove Schedule
                  </Button>
                )}
                <Button
                  variant="outline"
                  data-test-id="schedule-cancel-button"
                  onClick={() => setOpen(false)}
                  disabled={saving || deleting}
                >
                  Cancel
                </Button>
                <Button
                  data-test-id="schedule-save-button"
                  onClick={handleSave}
                  disabled={saving || deleting}
                  className="bg-toast-deep text-forest hover:bg-toast-deep/90"
                >
                  {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  {isCreating ? 'Create Schedule' : 'Update Schedule'}
                </Button>
              </DialogFooter>

              {/* Add another schedule button for single schedule view */}
              {!isCreating && schedules.length === 1 && (
                <div className="pt-4 border-t mt-4">
                  <Button
                    variant="outline"
                    data-test-id="schedule-add-another-button"
                    onClick={handleAddNewSchedule}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Another Schedule
                  </Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    )
  }
)
